import { useCallback, useMemo, useRef, useState } from 'react';

import { usePayerAddress } from 'lib/hooks/queries/payers/usePayerAddress';
import { usePayers } from 'lib/hooks/queries/payers/usePayers';

import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import TabsTableModal from 'components/shared/Modal/TabsTableModal/TabsTableModal';

import { PayerInitialValues } from '../lib/helper';

import PayerForm from './PayerForm/PayerForm';
import { usePayerForm } from './PayerForm/lib/usePayerForm';

const PayerModal = ({ payerId, isOpen, handleClose, practiceId }) => {
  const requestNewPayerFormRef = useRef();
  const formikRef = useRef();

  const [defaultAddress, setDefaultAddress] = useState(false);

  const { data: payersData, isLoading: isLoadingPayer } = usePayers({
    params: { id: payerId, practiceId },
    dependencies: [payerId],
    options: { enabled: !!payerId }
  });

  const payer = payersData?.payers?.[0] || null;

  const { data } = usePayerAddress({
    params: { payerId: payer?.id, fallbackToDefaultAddress: defaultAddress },
    dependencies: [payer?.id, defaultAddress],
    options: { enabled: !!payer?.id && defaultAddress }
  });

  const addressData = data || payer?.practicePayers?.[0]?.payerAddress;

  const initialValues = PayerInitialValues(payer, addressData);

  const { formik, isLoading: isFormLoading } = usePayerForm({
    payer,
    practiceId,
    externalInitialValues: initialValues,
    enableReinitialize: true,
    onSuccess: handleClose
  });

  // Store latest formik in ref to avoid remounting PayerForm
  formikRef.current = formik;

  const PayerModalHeader = useCallback(() => {
    if (!payer?.address || payer?.practicePayers?.[0]?.address_id) {
      return null;
    }

    return (
      <Button
        variant="secondary"
        size="small"
        color="neutral"
        onClick={() => setDefaultAddress(true)}
        text="Load Default Address"
        data-qa="load-default-address-button"
        className="ml-auto mr-6 h-6"
      />
    );
  }, [payer]);

  const UpdatePayerFormComponent = useCallback(
    ({ openModal }) => {
      return (
        <div className="flex h-full flex-col gap-4 px-4 pt-4">
          <PayerForm
            ref={requestNewPayerFormRef}
            isUpdate
            practiceId={practiceId}
            isLoading={isFormLoading}
            openModal={openModal}
            formik={formikRef.current}
          />
        </div>
      );
    },
    [isFormLoading]
  );

  const updatePayerTab = useMemo(
    () => ({
      name: payer?.name || 'Update Payer',
      headerTitle: payer?.name || 'Update Payer',
      component: UpdatePayerFormComponent,
      hideBackButton: true,
      continueText: ({ lockNext, loading, handleContinue }) => (
        <>
          <Button
            variant="secondary"
            color="neutral"
            text="Cancel"
            onClick={handleClose}
            size="small"
          />
          <Button
            variant="primary"
            text="Update payer"
            onClick={handleContinue}
            isLoading={loading || isFormLoading}
            isDisabled={lockNext || !formikRef.current?.dirty}
            id="savePayerModalBtn"
            data-qa="create-payer-btn"
            size="small"
          />
        </>
      ),
      onContinue: () => {
        requestNewPayerFormRef.current?.submitForm();
      }
    }),
    [UpdatePayerFormComponent, payer?.name, isFormLoading]
  );

  const tabs = useMemo(() => [updatePayerTab], [updatePayerTab]);

  if (!payer && !isLoadingPayer) {
    return null;
  }

  return (
    <TabsTableModal
      hasBackButton
      trimSteps
      bodyClassName="overflow-hidden"
      headerClassName="px-5 py-3"
      isOpen={isOpen}
      handleClose={handleClose}
      tabs={isLoadingPayer ? [] : tabs}
      isLoading={isLoadingPayer}
      headerActions={isLoadingPayer ? null : PayerModalHeader}
    />
  );
};

export default withErrorBoundary(PayerModal);
